/**
 * Diagnostics action - aggregated system diagnostics.
 * GET /observability/diagnostics (admin-only)
 */
import type { ActionDefinition, ModuleContext } from '@gzl10/nexus-sdk'
import type { Context } from 'hono'
import type { Knex } from 'knex'
import type { Redis } from 'ioredis'
import type { CacheManager } from '@kernel/cache/cache-manager.js'

interface MigrationStatusResult {
  completed: Array<{ name: string; batch: number; executionTime: number | null; status: string; source: string }>
  pending: Array<{ name: string; source: string }>
  bySource: Map<string, Array<{ name: string; status: string; batch?: number; executionTime?: number | null }>>
}

interface DiagnosticsInput {
  knex: Knex
  cacheManager: CacheManager | null
  redisClient: Redis | null
  socketInfo: { connected_users: number; total_connections: number }
  detailed?: boolean
  getMigrationStatus?: (knex: Knex) => Promise<MigrationStatusResult>
}

interface PoolStats {
  used: number
  free: number
  pendingAcquires: number
  pendingCreates: number
}

interface DiagnosticsResult {
  database: {
    pool: PoolStats | null
    pending_migrations: number
    migration_details?: {
      completed: number
      pending: Array<{ name: string; source: string }>
      by_source: Record<string, Array<{ name: string; status: string; batch?: number; executionTime?: number | null }>>
    }
  }
  cache: Record<string, unknown>
  redis: { status: string; memory_human?: string; connected_clients?: number } | { status: 'not_configured' }
  sockets: { connected_users: number; total_connections: number }
  process: { uptime_seconds: number; memory_mb: { rss: number; heap_used: number; heap_total: number } }
}

function getPoolStats(knex: Knex): PoolStats | null {
  const pool = (knex.client as any)?.pool
  if (!pool || typeof pool.numUsed !== 'function') return null
  return {
    used: pool.numUsed(),
    free: pool.numFree(),
    pendingAcquires: pool.numPendingAcquires(),
    pendingCreates: pool.numPendingCreates()
  }
}

function parseRedisInfo(info: string, key: string): string | undefined {
  const match = info.match(new RegExp(`^${key}:(.+)$`, 'm'))
  return match?.[1]?.trim()
}

async function getRedisStats(client: Redis | null): Promise<DiagnosticsResult['redis']> {
  if (!client) return { status: 'not_configured' }
  try {
    const info = await client.info('memory')
    const clientsInfo = await client.info('clients')
    return {
      status: (client as any).status ?? 'unknown',
      memory_human: parseRedisInfo(info, 'used_memory_human'),
      connected_clients: parseInt(parseRedisInfo(clientsInfo, 'connected_clients') ?? '0', 10)
    }
  } catch {
    return { status: (client as any).status ?? 'error' }
  }
}

function getSocketStats(ctx: ModuleContext): DiagnosticsResult['sockets'] {
  try {
    if (!ctx.core.socket.isInitialized()) return { connected_users: 0, total_connections: 0 }
    return {
      connected_users: ctx.core.socket.getConnectedUsers().length,
      total_connections: (ctx.core.socket.getIO() as any)?.engine?.clientsCount ?? 0
    }
  } catch {
    return { connected_users: 0, total_connections: 0 }
  }
}

export async function buildDiagnostics(input: DiagnosticsInput): Promise<DiagnosticsResult> {
  const mem = process.memoryUsage()

  const [migrationStatus, redisStats] = await Promise.all([
    input.getMigrationStatus ? input.getMigrationStatus(input.knex).catch(() => null) : Promise.resolve(null),
    getRedisStats(input.redisClient)
  ])

  return {
    database: {
      pool: getPoolStats(input.knex),
      pending_migrations: migrationStatus?.pending.length ?? -1,
      ...(input.detailed && migrationStatus ? {
        migration_details: {
          completed: migrationStatus.completed.length,
          pending: migrationStatus.pending.map(m => ({ name: m.name, source: m.source })),
          by_source: Object.fromEntries(migrationStatus.bySource)
        }
      } : {})
    },
    cache: input.cacheManager?.stats() ?? {},
    redis: redisStats,
    sockets: input.socketInfo,
    process: {
      uptime_seconds: Math.round(process.uptime()),
      memory_mb: {
        rss: Math.round(mem.rss / 1024 / 1024),
        heap_used: Math.round(mem.heapUsed / 1024 / 1024),
        heap_total: Math.round(mem.heapTotal / 1024 / 1024)
      }
    }
  }
}

export const diagnosticsAction: ActionDefinition = {
  key: 'diagnostics',
  label: { en: 'System Diagnostics', es: 'Diagnóstico del sistema' },
  icon: 'mdi:stethoscope',
  scope: 'module',
  method: 'GET',
  output: {},
  handler: async (ctx, _input, c?: Context<any>) => {
    const user = c?.get('user') as { id?: string } | undefined
    const ability = c?.get('ability') as { can?: (a: string, s: string) => boolean } | undefined
    if (!user) throw new ctx.core.errors.UnauthorizedError('AUTH_REQUIRED')
    if (!ability?.can?.('manage', 'all')) {
      throw new ctx.core.errors.ForbiddenError('Admin access required')
    }

    const detailed = c?.req.query('detailed') === 'true'
    const cacheManager = ctx.services.getOptional<CacheManager>('cacheManager') ?? null
    const redisClient = cacheManager?.redis ?? null
    const getMigrationStatus = ctx.services.getOptional<(knex: Knex) => Promise<MigrationStatusResult>>('showMigrationStatus') ?? undefined

    return buildDiagnostics({
      knex: ctx.db.knex,
      cacheManager,
      redisClient,
      socketInfo: getSocketStats(ctx),
      detailed,
      getMigrationStatus
    })
  }
}
